import { ROLE_LABEL, type Permission } from '@bnn/shared';
import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { SyncManager } from '../count/SyncManager';
import { useOnline } from '../lib/online';
import { useOutbox } from '../outbox';
import { Badge, Button } from './ui';

interface NavItem {
  to: string;
  label: string;
  perm?: Permission;
  /** milestone that delivers this page; shown as a badge until then */
  soon?: string;
}

export const NAV: NavItem[] = [
  { to: '/', label: 'หน้าแรก' },
  { to: '/count', label: 'นับสินค้า', perm: 'count.scan' },
  { to: '/sessions', label: 'รอบการนับ' },
  { to: '/items', label: 'สินค้า' },
  { to: '/barcodes', label: 'Barcode', perm: 'barcodes.view' },
  { to: '/warehouses', label: 'คลังสินค้า' },
  { to: '/imports', label: 'นำเข้า Master', perm: 'master.import' },
  { to: '/reports', label: 'รายงาน', perm: 'reports.view', soon: 'M4' },
  { to: '/sync', label: 'สถานะ Sync', perm: 'count.scan' },
  { to: '/users', label: 'ผู้ใช้', perm: 'users.manage' },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const box = useOutbox();
  const online = useOnline();
  const pending = box.pendingCount();

  const doLogout = () => {
    // Pending entries stay on this device and are sent the next time this user logs in here.
    if (pending > 0 && !confirm(`มี ${pending} รายการที่ยังไม่ได้ส่งขึ้นระบบ\nรายการจะยังอยู่ในเครื่องนี้ และส่งเองเมื่อคุณ login ที่เครื่องนี้อีกครั้ง\n\nออกจากระบบตอนนี้?`)) return;
    void logout();
  };
  const items = NAV.filter((n) => !n.perm || can(n.perm));

  const links = (
    <nav className="flex flex-col gap-0.5 p-2">
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === '/'}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            [
              'flex items-center gap-2 rounded-lg px-3 py-2.5 text-[15px]',
              isActive ? 'bg-brand/10 font-semibold text-brand' : 'text-ink hover:bg-chip',
              n.soon && !isActive ? 'text-muted' : '',
            ].join(' ')
          }
        >
          <span className="mr-auto">{n.label}</span>
          {n.soon && <Badge>{n.soon}</Badge>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen md:grid md:grid-cols-[232px_1fr] md:grid-rows-[auto_1fr]">
      <header className="sticky top-0 z-20 flex items-center gap-3 bg-ink px-4 py-2.5 text-white md:col-span-2">
        <button className="-ml-1 rounded p-1.5 md:hidden" onClick={() => setOpen((o) => !o)} aria-label="เมนู" aria-expanded={open}>
          <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <b className="text-[17px]">BNN Barcode Inventory</b>
        {!online && <Badge tone="bad">Offline</Badge>}
        {can('count.scan') && pending > 0 && (
          <Link to="/sync" className="rounded-full bg-amber-400 px-2 py-0.5 text-xs font-semibold text-ink">
            รอ Sync {pending}
          </Link>
        )}
        <div className="ml-auto flex items-center gap-3 text-right text-sm">
          <div className="hidden leading-tight sm:block">
            <div>{user?.display_name}</div>
            <div className="text-xs opacity-75">{user && ROLE_LABEL[user.role]}</div>
          </div>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={doLogout}>
            ออกจากระบบ
          </Button>
        </div>
      </header>

      <aside className="hidden border-r border-line bg-white md:block">{links}</aside>
      {open && (
        <div className="fixed inset-0 top-[52px] z-10 bg-black/40 md:hidden" onClick={() => setOpen(false)}>
          <aside className="h-full w-64 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            {links}
          </aside>
        </div>
      )}

      <SyncManager />
      <main className="mx-auto w-full max-w-5xl min-w-0 px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}
